import React, { useEffect } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head, { generateAlternateLink } from '@components/atoms/head/head';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { CatalogPageMetadataResponse } from '@interfaces/api/responses/catalog-page-metadata-response';
import { useAnalytics } from '@context/analytics/analytics.context';
import extractFromContext from '@helpers/utils/extract-from-context';
import CatalogPagesService from '@services/catalog-service';
import { CatalogPageSEOLinksResponse } from '@interfaces/api/responses/catalog-page-seo-links-response';
import { useRouter } from 'next/router';
import ProductSearch, { ProductSearchProps } from '@components/catalog/product-search/product-search';
import {
  extractUrlfromPrerenderDataReq,
  getSearchKeyword,
  isCatalogPage,
  isPersonalizedNewInsPage,
  isSearchPage,
} from '@helpers/routing';
import SEOLinks from '@components/catalog/seo-links/seo-links';
import SEOFooter from '@components/catalog/seo-footer/seo-footer';
import { parseURLQueryParameters } from '@helpers/utils/catalog/catalog-url-helpers';
import { SSRConfig } from 'next-i18next';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import RequestError from '@errors/request-error';
import DefaultLayout from '@layouts/default-layout';
import { dehydrate, DehydratedState, QueryClient, useHydrate } from '@tanstack/react-query';
import {
  ActiveDealQueryKeys,
  BreadcrumbsQueryKeys,
  CatalogPageQuerykeys,
  MarketingFiltersQueryKeys,
} from '@enums/react-query-keys';
import logger from '@helpers/utils/logger/client';
import SearchUrlAnalysisService from '@services/search-url-analysis-service';
import { UrlAnalysisResponse } from '@interfaces/api/responses/url-analysis-response';
import { getUrlAnalysisFilters } from '@helpers/utils/catalog/url-analysis-helpers';
import useSearchSessionId from '@hooks/catalog-context/use-search-session-id';
import { getBreadcrumbs } from '@api/breadcrumbs';
import { Deal } from '@interfaces/models/deal';
import { getActiveDeal } from '@api/deals';
import { ContentsMarketingFilter } from '@interfaces/models/contentsMarketingFilter';
import { getMarketingFilters } from '@api/contents';
import { CatalogGuidedSearchResponse } from '@interfaces/api/responses/catalog-page-guided-search-response';
import { getCatalogDatalayerEntrypoint, RESET_CATALOG_DATALAYER } from '@helpers/utils/catalog/catalog-tracking-utils';
import { getAppInitialProps } from '@helpers/initial-props';
import CatalogVoucherBanner from '@components/catalog/catalog-voucher-banner/catalog-voucher-banner';
import useCatalogVoucherBanner from '@hooks/catalog-context/use-catalog-voucher-banner';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import { ApplicationGetInitialProps } from '@pages/_app.page';
import getServerFeatureFlag from '@hooks/bucket-test/use-ssr-feature-flag';

export type CategoryPageProps = {
  dehydratedState: DehydratedState;
  catalogContextProps: Omit<ProductSearchProps['catalogContextProps'], 'searchSessionId'>;
  metadata: CatalogPageMetadataResponse;
  guidedSearch: CatalogGuidedSearchResponse;
  seoLinks: CatalogPageSEOLinksResponse;
  alertId: string;
  innerFeedContext: string;
};

const getSourceTrackingInfo = ({ alertId, innerFeedContext }: { alertId: string; innerFeedContext: string }) => {
  if (alertId) {
    return getCatalogDatalayerEntrypoint({ source_category: 'saved_searches', source_subcategory: alertId });
  }
  if (innerFeedContext) {
    return getCatalogDatalayerEntrypoint({ source_subcategory: innerFeedContext });
  }
  return {};
};

const CategoryPage: NextPageWithLayout<CategoryPageProps> = (props): React.JSX.Element => {
  const { catalogContextProps, metadata, seoLinks, dehydratedState, alertId, innerFeedContext } = props;
  const [searchSessionIdState, setSearchSessionIdState] = React.useState<string>('');

  const { showBanner, text: voucherBannerText } = useCatalogVoucherBanner();

  useHydrate(dehydratedState);

  const router = useRouter();
  const { updateDataLayer } = useAnalytics();
  const { assignSearchSessionId } = useSearchSessionId();

  useEffect(() => {
    const defaultPath = metadata.alternateVersions.find((i) => i.language === 'x-default')?.path;
    const isSearch = isSearchPage(router.asPath);
    const keyword = getSearchKeyword(router.query.q as string);
    const searchSessionId = assignSearchSessionId();
    setSearchSessionIdState(searchSessionId);
    updateDataLayer({
      ...RESET_CATALOG_DATALAYER,
      env_template: 'catalog',
      pageUrl: isSearch ? `/search/productList` : `/productList${defaultPath}`,
      screen_name: isSearch ? '/search_results/keyword' : '/search_results/catalog',
      screen_category: 'search',
      screen_subcategory: 'search_results',
      search_session_id: searchSessionId,
      keyword,
      keyword_user_input: keyword,
      ...getSourceTrackingInfo({ alertId, innerFeedContext }),
    });
  }, []);

  return (
    <DefaultLayout shouldShowBanner={showBanner}>
      {showBanner && <CatalogVoucherBanner text={voucherBannerText} />}
      <Head
        metadata={metadata?.metadata}
        alternateVersions={metadata?.alternateVersions}
      />
      <ProductSearch
        catalogContextProps={{ ...catalogContextProps, searchSessionId: searchSessionIdState }}
        footerContent={
          !isCatalogPage(metadata.path) && (
            <>
              <SEOLinks candidates={seoLinks?.candidates} />
              {!!metadata?.content?.body && !!metadata?.content?.headline && (
                <SEOFooter
                  title={metadata.content.headline}
                  body={metadata.content.body}
                />
              )}
            </>
          )
        }
      />
    </DefaultLayout>
  );
};

CategoryPage.Layout = [];
CategoryPage.PageType = 'CategoryPage';

// TODO: Tiny issue with "...(productId ? ["..., fix when available
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, query: contextQuery, res } = context;
  res.setHeader('x-vc-page', 'catalog');
  /*
    PLP is a dynamic route, when trying to fetch prerender data,
    /_next/data request will trigger getServerSideProps and cause getCatalogPageMetadata request failed
  */
  req.url = extractUrlfromPrerenderDataReq(contextQuery, 'categorySlug');

  const { locale, serverAxiosRequestData } = extractFromContext(context);
  const { serverAnonymousId, randomRangeId } = getOrCreateLDCookiesSsr(context);
  const { pathname, pathnameWithoutPagination, page, query, sortBy } = parseURLQueryParameters(req.url);

  const queryClient = new QueryClient();

  const { getFeatureFlag } = getServerFeatureFlag(context, serverAnonymousId, randomRangeId);

  try {
    const mandatoryRequests = [
      getAppInitialProps(context, queryClient),
      serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common', 'saved-searches']),
      CatalogPagesService.getApplicableSizeIds(pathnameWithoutPagination),
      CatalogPagesService.getCatalogPageMetadata(
        pathnameWithoutPagination,
        serverAxiosRequestData,
        query,
        serverAnonymousId,
      ),
      getActiveDeal(serverAxiosRequestData),
      getMarketingFilters(serverAxiosRequestData),
    ];

    const optionalRequests = [
      CatalogPagesService.getCatalogPageSEOLinks(pathnameWithoutPagination, serverAxiosRequestData),
      getBreadcrumbs(pathnameWithoutPagination, serverAxiosRequestData),
      CatalogPagesService.getGuidedSearch(pathnameWithoutPagination, serverAxiosRequestData),
      getFeatureFlag('enable-guided-search-on-plp'),
      getFeatureFlag('enable-plp-filtering-only-id-url-generation'),
      ...(!isSearchPage(pathnameWithoutPagination)
        ? [SearchUrlAnalysisService.urlAnalysis(pathnameWithoutPagination)]
        : [null]),
    ];

    const promiseResults = await Promise.allSettled([...mandatoryRequests, ...optionalRequests]);

    const [
      initialProps,
      translations,
      applicableSizeIds,
      metadata,
      deal,
      marketingFilters,
      seoLinks,
      breadcrumbs,
      guidedSearch,
      enableGuidedSearchOnPlp,
      isPlpFilteringOnlyIDURLGenerationEnabled,
      urlAnalysisResult,
    ] = promiseResults.map((promiseResult, index) => {
      if (promiseResult.status === 'fulfilled') {
        return promiseResult.value;
      }
      // Throw on error for tracking to pick it up for required requests, the rest can fail
      if (index < mandatoryRequests.length) {
        throw new RequestError(promiseResult.reason);
      }
      return null;
    }) as [
      ApplicationGetInitialProps,
      SSRConfig,
      number[],
      CatalogPageMetadataResponse,
      Deal,
      ContentsMarketingFilter[],
      CatalogPageSEOLinksResponse,
      Breadcrumb[] | null,
      CatalogGuidedSearchResponse | null,
      boolean,
      boolean,
      UrlAnalysisResponse | null,
    ];

    if (metadata?.alternateVersions) {
      metadata.alternateVersions = metadata?.alternateVersions.map((version) => {
        return {
          ...version,
          path: generateAlternateLink(version),
        };
      });
    }

    queryClient.setQueryData([BreadcrumbsQueryKeys.BREADCRUMBS], breadcrumbs);
    queryClient.setQueryData([ActiveDealQueryKeys.ACTIVE_DEAL], deal);
    queryClient.setQueryData([MarketingFiltersQueryKeys.MARKETING_FILTERS], marketingFilters);
    queryClient.setQueryData([CatalogPageQuerykeys.GET_CATALOG_METADATA], metadata);
    queryClient.setQueryData([CatalogPageQuerykeys.GET_GUIDED_SEARCH], guidedSearch);

    // We redirect to correct localized path, e.g. if user with US cookies tries to access french url, we redirect to US localized one
    const { path } = metadata;
    if (!isCatalogPage(pathname) && path !== pathnameWithoutPagination && !isPersonalizedNewInsPage(pathname)) {
      const { resolvedUrl } = context;
      const [, queryParams] = resolvedUrl.split('?');
      const hasQueryParams: boolean = !!queryParams;

      return {
        redirect: {
          destination: `${path}${page > 1 ? `p-${page}/` : ''}${hasQueryParams ? `?${queryParams}` : ''}`,
          permanent: true,
        },
      };
    }

    const filters = getUrlAnalysisFilters(urlAnalysisResult);
    const alertId = contextQuery?.alertId ?? '';
    const innerFeedContext = contextQuery?.innerFeedContext ?? '';

    return {
      props: {
        ...initialProps,
        ...translations,
        alertId,
        innerFeedContext,
        dehydratedState: dehydrate(queryClient),
        metadata: metadata ?? null,
        seoLinks: seoLinks ?? [],
        guidedSearch,
        catalogContextProps: {
          metadata: metadata ?? null,
          applicableSizeIds,
          filters,
          page,
          sortBy,
          query,
          products: [],
          facets: null,
          priceBound: {
            lower: 0,
            upper: 0,
          },
          isGuidedSearchEnabled: enableGuidedSearchOnPlp,
          isPlpFilteringOnlyIDURLGenerationEnabled,
        },
      },
    };
  } catch (e) {
    // Axios 404 errors should get redirected straight to error page
    if (isRequestError(e)) {
      if (!isRequest404Error(e)) {
        logger.error(
          e,
          'There was an api request error on the Category Page - getServerSideProps - [[...categorySlug]].page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    // Other errors will get logged
    logger.error(
      e,
      'The server failed to render the Category Page - getServerSideProps - [[...categorySlug]].page.tsx',
    );
    throw e;
  }
};

export default CategoryPage;
